import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchOrders, OrderStatus } from '../api/orders';
import { ORDER_STATUS_LABELS } from '../components/StatusBadge';
import { ApiError } from '../api/client';
import { Spinner } from '../components/ui/Spinner';
import { useAuth, UserRole } from '../auth/AuthContext';
import { OrderListCard } from '../components/OrderListCard';

const PAGE_SIZE = 20;

// Hook utilitaire : retourne `value` après un délai (réduit les requêtes pendant la frappe)
const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
};

const ALL_STATUSES: (OrderStatus | 'all')[] = [
  'all',
  'pending',
  'accepted',
  'picked_up',
  'in_transit',
  'delivered',
  'cancelled',
  'failed'
];

const filterLabel = (s: OrderStatus | 'all') => (s === 'all' ? 'Tous' : ORDER_STATUS_LABELS[s]);

const ROLE_COPY: Record<
  UserRole,
  { title: string; description: string; emptyTitle: string; emptyBody: string; showCreateLink: boolean }
> = {
  client: {
    title: 'Mes livraisons',
    description:
      'Ce qui est encore en route. Ouvrez une carte pour suivre la course. Ce qui est déjà fini se trouve dans Historique.',
    emptyTitle: 'Aucune livraison pour l’instant',
    emptyBody:
      'Créez une commande de test ou attendez qu’un commerçant vous adresse un envoi. Le parcours complet s’affichera ici.',
    showCreateLink: true
  },
  business: {
    title: 'Livraisons entreprise',
    description:
      'Vue opérationnelle des envois liés à votre compte : statut logistique, destinataires et étapes jusqu’à la remise.',
    emptyTitle: 'Pas encore d’envoi enregistré',
    emptyBody: 'Créez une commande depuis « Nouvelle commande » ou importez vos flux via l’API partenaire.',
    showCreateLink: true
  },
  deliverer: {
    title: 'Mes courses',
    description:
      'Prenez une course ouverte, puis avancez-la : colis récupéré, en route, remis à la porte. Le passé est dans Historique.',
    emptyTitle: 'Aucune course pour le moment',
    emptyBody: 'Quand une course est à prendre dans votre zone, elle apparaît ici. Ouvrez-la pour dire que vous la prenez.',
    showCreateLink: false
  },
  admin: {
    title: 'Toutes les commandes',
    description:
      'Vue globale plateforme : filtrez par statut pour auditer le pipeline (commande → acceptation → collecte → route → livré).',
    emptyTitle: 'Base vide ou filtre trop strict',
    emptyBody:
      'Exécutez le script de seed démo ou créez des commandes via l’API pour peupler cette vue et tester le workflow.',
    showCreateLink: true
  }
};

export const OrdersListPage = () => {
  const { user } = useAuth();
  const role = user?.role ?? 'client';
  const copy = ROLE_COPY[role];

  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebouncedValue(searchInput.trim(), 350);

  // Reset à la première page dès qu'on change un filtre
  useEffect(() => {
    setPage(0);
  }, [statusFilter, debouncedSearch]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['orders', statusFilter, debouncedSearch, page],
    queryFn: () =>
      fetchOrders({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: debouncedSearch || undefined
      }),
    placeholderData: keepPreviousData
  });

  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = statusFilter !== 'all' || debouncedSearch.length > 0;

  const roleBadge = useMemo(() => {
    const labels: Record<UserRole, string> = {
      admin: 'Administrateur',
      client: 'Client',
      deliverer: 'Livreur',
      business: 'Entreprise'
    };
    return labels[role];
  }, [role]);

  return (
    <div className="animate-fade-in">
      <div className="mb-5 flex flex-col gap-4 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 px-5 py-5 text-white shadow-sm sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-medium text-blue-100">Bonjour {user?.firstName ?? roleBadge}</p>
          <h1 className="mt-1 text-2xl font-semibold sm:text-3xl">{copy.title}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-blue-50">{copy.description}</p>
        </div>
        <button type="button" className="btn-ghost shrink-0 bg-white/95" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? (
            <>
              <Spinner className="h-4 w-4 border-2 border-slate-400 border-t-transparent" />
              Rafraîchissement…
            </>
          ) : (
            'Rafraîchir'
          )}
        </button>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white/60 p-3 shadow-sm backdrop-blur-sm sm:p-4">
        <div className="mb-3">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Rechercher</span>
            <div className="relative mt-1.5">
              <input
                type="search"
                className="input pl-9"
                placeholder="Code de suivi, nom, adresse…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
              >
                <circle cx="11" cy="11" r="7" />
                <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
              </svg>
              {searchInput && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100"
                  onClick={() => setSearchInput('')}
                  aria-label="Effacer la recherche"
                >
                  ✕
                </button>
              )}
            </div>
          </label>
        </div>

        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Filtrer par étape</p>
        <div className="flex flex-wrap gap-2">
          {ALL_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                statusFilter === s
                  ? 'border-brand-500 bg-brand-500 text-white shadow-md ring-2 ring-brand-500/20'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              }`}
              onClick={() => setStatusFilter(s)}
            >
              {filterLabel(s)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {isLoading && (
          <div className="card flex flex-col items-center justify-center gap-3 p-12 text-slate-500">
            <Spinner />
            <p className="text-sm">Chargement des commandes…</p>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {error instanceof ApiError ? error.message : 'Erreur inconnue'}
          </div>
        )}

        {data && data.data.length === 0 && (
          <div className="card overflow-hidden p-0">
            <div className="border-b border-slate-100 bg-gradient-to-r from-brand-50/80 via-white to-slate-50 px-6 py-8 text-center sm:px-10">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-md ring-1 ring-slate-200/80">
                <svg className="h-7 w-7 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20 13V7a2 2 0 00-2-2h-3M4 13V7a2 2 0 012-2h3m0 0V5a2 2 0 012-2h4a2 2 0 012 2v2M4 13v6a2 2 0 002 2h12a2 2 0 002-2v-6"
                  />
                </svg>
              </div>
              <h2 className="mt-5 text-lg font-semibold text-slate-900">{copy.emptyTitle}</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">{copy.emptyBody}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {copy.showCreateLink && (role === 'client' || role === 'business' || role === 'admin') && (
                  <Link to="/orders/new" className="btn-primary">
                    Nouvelle commande
                  </Link>
                )}
                {role === 'deliverer' && (
                  <Link to="/deliverer" className="btn-primary">
                    Ouvrir le dashboard livreur
                  </Link>
                )}
                <button type="button" className="btn-ghost" onClick={() => refetch()}>
                  Réessayer le chargement
                </button>
              </div>
            </div>
            <div className="bg-slate-50/80 px-6 py-4 text-center text-xs text-slate-500">
              Schéma du parcours : <span className="font-medium text-slate-700">commande</span> → acceptation → collecte →
              en route → <span className="font-medium text-slate-700">livré</span>
            </div>
          </div>
        )}

        {data && data.data.length > 0 && (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm">
              <p className="text-slate-600">
                <span className="font-semibold text-slate-900">{total}</span> commande
                {total !== 1 ? 's' : ''}
                {hasFilters && <span className="text-slate-500"> (filtrées)</span>}
                {' — page '}
                <span className="font-semibold text-slate-900">{page + 1}</span>
                {' / '}
                {totalPages}
              </p>
              {hasFilters && (
                <button
                  type="button"
                  className="text-xs font-medium text-brand-600 hover:underline"
                  onClick={() => {
                    setSearchInput('');
                    setStatusFilter('all');
                  }}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              {data.data.map((order) => (
                <OrderListCard key={order.id} order={order} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || isFetching}
                >
                  ← Précédent
                </button>
                <span className="text-xs text-slate-500">
                  Page {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || isFetching}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
